import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import datetime from "config/datetime";
import useAuthorName from "hooks/useAuthorName";
import useUnitParser from "hooks/useUnitParser";
import type { Variants } from "types";
import useOneRepMaxEntries from "components/charting/OneRepMaxChartV2/useOneRepMaxEntries";
import useOneRepMaxChartWeightMetric from "components/charting/OneRepMaxChartV2/useOneRepMaxChartWeightMetric";

export interface OneRepMaxEntriesTableRow {
  id: string;
  date: string;
  variant: Variants | null;
  addedBy: string | null;
  value: string;
  /** Names the entry for screen readers and the delete dialog, e.g. "150 kg on Aug 10, 2023". */
  summary: string;
}

function useOneRepMaxEntriesTableRows(): OneRepMaxEntriesTableRow[] {
  const { t } = useTranslation();
  const parseUnit = useUnitParser();
  const entries = useOneRepMaxEntries();
  const creatorIds = useMemo(() => entries.flatMap((entry) => (entry.creatorId ? [entry.creatorId] : [])), [entries]);
  const creatorNames = useAuthorName(creatorIds);
  const weightMetric = useOneRepMaxChartWeightMetric();

  return useMemo(
    () =>
      entries.map((entry) => {
        const { value, unit } = parseUnit(entry.weight, weightMetric).results[0];
        const date = datetime(entry.createdDate).format("ll");
        const weight = `${value} ${unit}`;

        return {
          id: entry.id,
          date,
          variant: entry.variant,
          addedBy: entry.creatorId ? creatorNames[entry.creatorId] : null,
          value: weight,
          summary: t("charting.one_rep_max_entry_summary", {
            context: entry.variant ? "variant" : undefined,
            value: weight,
            date,
            variant: entry.variant ? t("global.variant_side", { context: entry.variant }) : undefined,
          }),
        };
      }),
    [creatorNames, entries, parseUnit, t, weightMetric]
  );
}

export default useOneRepMaxEntriesTableRows;
